import { Roles } from 'typings/enums';
import { observable, action, computed, makeObservable } from 'mobx';

export class User {
    readonly _rolesMap: Map<Roles, Roles>;
    private readonly _userId: string;
    @observable private _firstName: Nullable<string>;
    @observable private _displayName: Nullable<string>;
    @observable private _displayInitial: Nullable<string>;
    @observable private _employeeSourceId: Nullable<string>;
    @observable private _isHrAssistant: boolean;
    @observable private _hasDirectReports: boolean;

    constructor(
        userId: string,
        displayName: Nullable<string>,
        employeeSourceId: Nullable<string>,
        roles: Nullable<string[]>,
        isHrAssistant: Nullable<boolean>,
        displayInitial: Nullable<string> = null) {
        makeObservable(this);
        this._userId = userId;
        this._displayName = displayName;
        this._employeeSourceId = employeeSourceId;
        this._rolesMap = new Map(!roles ? [] : roles.map(role => [role, role] as [Roles, Roles]));
        this._isHrAssistant = isHrAssistant ?? false;
        this._displayInitial = displayInitial;
        this._firstName = '';
    }

    get userId(): string {
        return this._userId;
    }

    @computed
    get firstName(): Nullable<string> {
        return this._firstName;
    }

    @computed
    get displayName(): Nullable<string> {
        return this._displayName;
    }

    @computed
    get displayInitial(): Nullable<string> {
        return this._displayInitial;
    }

    @computed
    get employeeSourceId(): Nullable<string> {
        return this._employeeSourceId;
    }

    get isRoleOnlyManager(): boolean {
        return this.checkIfAnyRolesExist(Roles.HumanResource) === false
            && this.checkIfAnyRolesExist(Roles.Support) === false;
    }

    get missingManagerRole(): boolean {
        return !this.checkIfAnyRolesExist(...[Roles.ManagerOfManager, Roles.Manager]) &&
            !this.isSupportPersonnel && !this.hasHrRole;
    }

    @computed
    get isHrAssistant(): boolean {
        return this._isHrAssistant;
    }

    get isHrbp(): boolean {
        return this._rolesMap.has(Roles.Hrbp);
    }

    get isSupportPersonnel(): boolean {
        return this.checkIfAnyRolesExist(Roles.Support);
    }

    get hasHrRole(): boolean {
        return this._rolesMap.has(Roles.HumanResource);
    }

    get isHrAdmin(): boolean {
        return this.hasHrRole && !this._isHrAssistant;
    }

    get isSupportOnly(): boolean {
        const nonSupportRoles =
            Object.keys(Roles).filter(role => role !== Roles.Support && role !== Roles.TeamDev) as Roles[];
        return this._rolesMap.has(Roles.Support) && !this.checkIfAnyRolesExist(...nonSupportRoles);
    }

    get isHrOrSupport(): boolean {
        return this._rolesMap.has(Roles.HumanResource) || this._rolesMap.has(Roles.Support);
    }

    get isFirstLevelManager(): boolean {
        return !this.checkIfAnyRolesExist(Roles.HumanResource, Roles.Hrbp, Roles.ManagerOfManager, Roles.Support);
    }

    get hasNoRolesAssigned(): boolean {
        return this._rolesMap.size === 0;
    }

    get hasDirectReports(): boolean {
        return this._hasDirectReports;
    }

    get isTeamDev(): boolean {
        return this.checkIfAnyRolesExist(Roles.TeamDev)
        && this.checkIfAnyRolesExist(Roles.Support);
    }

    get hasNonSupportRole(): boolean {
        return !this.isSupportPersonnel;
    }

    @action
    setHasDirectReports(value: boolean): void {
        this._hasDirectReports = value;
    }

    @action
    setEmployeeSourceId(employeeSourceId: string): void {
        this._employeeSourceId = employeeSourceId;
    }

    @action
    setName(displayName: string, firstName: string): void {
        this._displayName = displayName;
        this._firstName = firstName;
    }

    @action
    setAsHrAssistant(): void {
        this._isHrAssistant = true;
    }

    private checkIfAnyRolesExist(...roles: Roles[]): boolean {
        return roles.find(role => this._rolesMap.has(role)) !== undefined;
    }
}